import React, { useEffect, useLayoutEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import type {
  GroupField,
  FormField,
  FormRowField,
  FormComponentType,
  FormBuilderSelection,
  PatientInfoField,
} from '../types'
import {
  Checkbox,
  Modal,
  RadioGroup,
  RichTextEditor,
} from '@enamelai/design-system'
import type { RichTextEditorToolbarGroup } from '@components/ui/RichTextEditor'
import { ToggleSwitch } from '@components/ui/ToggleSwitch'
import {
  INPUT_FIELD_AUTOFILL_OPTIONS,
  DATE_FIELD_AUTOFILL_OPTIONS,
  GENDER_SELECTION_AUTOFILL_OPTIONS,
} from '../constants'
import { createFormField } from '../createFormField'
import { logger } from '@utils/safeLogger'
import './GroupFields.css'

const createFormBuilderId = (prefix: string): string =>
  `${prefix}-${globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`}`

const isFormRowItem = (
  item: FormField | GroupField | FormRowField,
): item is FormRowField =>
  'type' in item && item.type === 'Form Row' && 'fields' in item

const isNestedGroupItem = (
  item: FormField | GroupField | FormRowField,
): item is GroupField => 'fields' in item && 'name' in item

const countFormItems = (fields: GroupField['fields']): number =>
  fields.reduce((count, item) => {
    if (isFormRowItem(item)) {
      return count + item.fields.length
    }
    if (isNestedGroupItem(item)) {
      return count + countFormItems(item.fields)
    }
    return count + 1
  }, 0)

/** Field types that show the options popover (Required + Auto-fill). */
const FIELD_TYPES_WITH_OPTIONS: FormComponentType[] = [
  'Input Field',
  'Text Area',
  'Date Field',
  'Radio Group',
  'Gender Selection',
]

/** Field types that show the settings chevron on the builder chip. */
const FIELD_TYPES_WITH_CHEVRON: FormComponentType[] = [
  'Input Field',
  'Date Field',
]

function getAutofillOptionsForFieldType(
  type: FormComponentType,
): Array<{ value: PatientInfoField | null; label: string }> {
  switch (type) {
    case 'Input Field':
    case 'Text Area':
      return INPUT_FIELD_AUTOFILL_OPTIONS
    case 'Date Field':
      return DATE_FIELD_AUTOFILL_OPTIONS
    case 'Gender Selection':
    case 'Radio Group':
      return GENDER_SELECTION_AUTOFILL_OPTIONS
    default:
      return [{ value: null, label: 'None' }]
  }
}

/** Sanitize string for use as HTML id so querySelector('#id') works (e.g. no dots). */
const FieldOptionsPopover: React.FC<{
  target: HTMLElement | null
  onClose: () => void
  children: React.ReactNode
}> = ({ target, onClose, children }) => {
  const panelRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({ top: 0, left: 0 })

  useLayoutEffect(() => {
    if (!target) return undefined

    const update = (): void => {
      const rect = target.getBoundingClientRect()
      const width = 260
      let left = rect.left
      if (left + width > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - width - 8)
      }
      setPosition({ top: rect.bottom + 4, left })
    }

    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [target])

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent): void => {
      const node = event.target as Node
      if (panelRef.current?.contains(node) || target?.contains(node)) return
      onClose()
    }

    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [onClose, target])

  if (!target) return null

  return createPortal(
    <div
      ref={panelRef}
      className="group-field-popover"
      role="dialog"
      aria-label="Field options"
      style={{ top: position.top, left: position.left }}
    >
      {children}
    </div>,
    document.body,
  )
}

const FORM_BUILDER_RICH_TEXT_TOOLBAR: RichTextEditorToolbarGroup[] = [
  ['fontSize', 'textColor', 'bgColor'],
  ['alignment'],
  ['bold', 'italic', 'underline', 'strikethrough'],
  ['link'],
]

const COMPONENT_ICONS: Record<string, string> = {
  'Group Fields': 'mgc_vector_group_line',
  'Form Row': 'mgc_rows_2_fill',
  'Input Field': 'mgc_textbox_line',
  'Text Area': 'mgc_paragraph_line',
  'Date Field': 'mgc_calendar_2_fill',
  Checkbox: 'mgc_checkbox_fill',
  'Checkbox Group': 'mgc_list_check_fill',
  Dropdown: 'mgc_selector_vertical_line',
  'Radio Group': 'mgc_radiobox_fill',
  'Rich Text': 'mgc_text_color_fill',
  'Gender Selection': 'mgc_radiobox_fill',
  Attachments: 'mgc_attachment_2_line',
  'Signature Field': 'mgc_signature_line',
  'Dentist/Admin Signature': 'mgc_signature_line',
}

interface GroupFieldsProps {
  group: GroupField
  onUpdate: (group: GroupField) => void
  onDelete: (groupId: string) => void
  onCopy: (group: GroupField) => void
  onFieldAdd: (groupId: string, field: FormField) => void
  onDrop?: (e: React.DragEvent<HTMLDivElement>, groupId: string) => void
  onDragOver?: (e: React.DragEvent<HTMLDivElement>) => void
  onDragLeave?: (e: React.DragEvent<HTMLDivElement>) => void
  isDraggingOver?: boolean
  onFieldUpdate?: (groupId: string, fieldId: string, label: string) => void
  onFieldOptionsUpdate?: (
    groupId: string,
    fieldId: string,
    updates: Partial<Pick<FormField, 'required' | 'autoFillSource'>>,
  ) => void
  onFieldReorder?: (
    groupId: string,
    fieldId: string,
    newIndex: number,
    parentGroupId?: string,
  ) => void
  onFieldDelete?: (
    groupId: string,
    fieldId: string,
    parentGroupId?: string,
  ) => void
  onGroupAdd?: (groupId: string, nestedGroup: GroupField) => void
  onNestedGroupUpdate?: (
    parentGroupId: string,
    nestedGroupId: string,
    updatedGroup: GroupField,
  ) => void
  onNestedGroupDelete?: (parentGroupId: string, nestedGroupId: string) => void
  parentGroupId?: string
  level?: number
  onGroupDragStart?: (
    e: React.DragEvent<HTMLDivElement>,
    groupId: string,
  ) => void
  onGroupDragEnd?: (e: React.DragEvent<HTMLDivElement>) => void
  onGroupDragOver?: (
    e: React.DragEvent<HTMLDivElement>,
    groupId: string,
  ) => void
  onGroupDrop?: (e: React.DragEvent<HTMLDivElement>, groupId: string) => void
  isGroupDragging?: boolean
  isGroupDragOver?: boolean
  hasGroupBeingDragged?: boolean
  selectedFieldId?: string | null
  onFieldSelect?: (field: FormBuilderSelection | null) => void
  /** When true, hide delete and field options buttons (e.g. view/preview mode). */
  isViewMode?: boolean
}

const GroupFields: React.FC<GroupFieldsProps> = ({
  group,
  onUpdate,
  onDelete,
  onCopy,
  onFieldAdd,
  onDrop,
  onDragOver,
  onDragLeave,
  isDraggingOver,
  onFieldUpdate,
  onFieldOptionsUpdate,
  onFieldReorder,
  onFieldDelete,
  onGroupAdd,
  onNestedGroupUpdate,
  onNestedGroupDelete,
  parentGroupId,
  level = 0,
  onGroupDragStart,
  onGroupDragEnd,
  onGroupDragOver,
  onGroupDrop,
  isGroupDragging,
  isGroupDragOver,
  hasGroupBeingDragged,
  selectedFieldId,
  onFieldSelect,
  isViewMode = false,
}) => {
  const standalone = group.standalone === true
  const [isEditing, setIsEditing] = useState(false)
  const [groupName, setGroupName] = useState(group.name)
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null)
  const [editingFieldLabel, setEditingFieldLabel] = useState<string>('')
  const [draggedFieldId, setDraggedFieldId] = useState<string | null>(null)
  const [draggedFormRowId, setDraggedFormRowId] = useState<string | null>(null)
  const [dragOverFieldId, setDragOverFieldId] = useState<string | null>(null)
  const [pendingDelete, setPendingDelete] = useState<
    | { kind: 'group' }
    | { kind: 'form-row'; rowId: string; fieldCount: number }
    | null
  >(null)
  const [fieldOptionsPopoverFieldId, setFieldOptionsPopoverFieldId] = useState<
    string | null
  >(null)
  const fieldOptionsPopoverTargetRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!isViewMode) return
    setIsEditing(false)
    setEditingFieldId(null)
    setFieldOptionsPopoverFieldId(null)
    fieldOptionsPopoverTargetRef.current = null
  }, [isViewMode])

  const handleGroupDeleteClick = (): void => {
    if (countFormItems(group.fields) === 0) {
      onDelete(group.id)
      return
    }
    setPendingDelete({ kind: 'group' })
  }

  const handleFormRowDeleteClick = (row: FormRowField): void => {
    if (!onFieldDelete) return
    if (row.fields.length === 0) {
      onFieldDelete(group.id, row.id, parentGroupId)
      return
    }
    setPendingDelete({
      kind: 'form-row',
      rowId: row.id,
      fieldCount: row.fields.length,
    })
  }

  const confirmPendingDelete = (): void => {
    if (pendingDelete?.kind === 'group') {
      onDelete(group.id)
    } else if (pendingDelete?.kind === 'form-row' && onFieldDelete) {
      onFieldDelete(group.id, pendingDelete.rowId, parentGroupId)
    }
    setPendingDelete(null)
  }

  const openFieldOptionsPopover = (
    e: React.MouseEvent<HTMLButtonElement>,
    fieldId: string,
  ): void => {
    e.stopPropagation()
    const button = e.currentTarget
    const fieldElement =
      button.closest('.group-field-item') ??
      button.closest('.form-row-field-item')
    fieldOptionsPopoverTargetRef.current =
      (fieldElement as HTMLElement) ?? button
    setFieldOptionsPopoverFieldId(fieldId)
  }

  const closeFieldOptionsPopover = (): void => {
    setFieldOptionsPopoverFieldId(null)
    fieldOptionsPopoverTargetRef.current = null
  }

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setGroupName(e.target.value)
  }

  const handleNameBlur = (): void => {
    setIsEditing(false)
    if (groupName.trim() !== group.name) {
      onUpdate({ ...group, name: groupName.trim() })
    }
  }

  const handleNameKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (e.key === 'Enter') {
      handleNameBlur()
    }
  }

  const handleGroupNameInputFocus = (
    e: React.FocusEvent<HTMLInputElement>,
  ): void => {
    e.target.select()
  }

  const isGroupField = (
    item: FormField | GroupField | FormRowField,
  ): item is GroupField => {
    return 'fields' in item && 'name' in item
  }

  const isFormRowField = (
    item: FormField | GroupField | FormRowField,
  ): item is FormRowField => {
    return 'type' in item && item.type === 'Form Row' && 'fields' in item
  }

  const isRichTextOnlyGroup = (g: GroupField): boolean => {
    if (g.fields.length !== 1) return false
    const item = g.fields[0]
    return (
      item != null &&
      !isGroupField(item) &&
      !isFormRowField(item) &&
      (item as FormField).type === 'Rich Text'
    )
  }

  const updateFieldPlaceholderInGroup = (
    targetGroup: GroupField,
    fieldId: string,
    placeholder: string,
  ): GroupField => {
    const updatedFields = targetGroup.fields.map((item) => {
      if (isFormRowField(item)) {
        const updatedRowFields = item.fields.map((f) =>
          f.id === fieldId ? { ...f, placeholder } : f,
        )
        return {
          ...item,
          fields: updatedRowFields,
        }
      }
      if (isGroupField(item)) {
        return updateFieldPlaceholderInGroup(item, fieldId, placeholder)
      }
      if (item.id === fieldId) {
        return {
          ...(item as FormField),
          placeholder,
        }
      }
      return item
    })

    return {
      ...targetGroup,
      fields: updatedFields,
    }
  }

  type FieldLocationInGroup =
    | { field: FormField; source: 'direct'; index: number }
    | {
        field: FormField
        source: 'formRow'
        formRowId: string
        formRow: FormRowField
        indexInRow: number
      }

  const findFieldInGroup = (
    g: GroupField,
    fieldId: string,
  ): FieldLocationInGroup | null => {
    const directIndex = g.fields.findIndex(
      (f) => 'id' in f && f.id === fieldId && !('name' in f),
    )
    if (directIndex !== -1) {
      return {
        field: g.fields[directIndex] as FormField,
        source: 'direct',
        index: directIndex,
      }
    }
    for (const item of g.fields) {
      if (isFormRowField(item)) {
        const idx = item.fields.findIndex((f) => f.id === fieldId)
        if (idx !== -1) {
          return {
            field: item.fields[idx],
            source: 'formRow',
            formRowId: item.id,
            formRow: item,
            indexInRow: idx,
          }
        }
      }
    }
    return null
  }

  const handleFieldLabelClick = (field: FormField): void => {
    if (isViewMode) return
    setEditingFieldId(field.id)
    setEditingFieldLabel(field.label || field.type)
  }

  const handleFieldInputFocus = (
    e: React.FocusEvent<HTMLInputElement>,
  ): void => {
    e.target.select()
  }

  const handleFieldLabelChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setEditingFieldLabel(e.target.value)
  }

  const handleFieldLabelBlur = (fieldId: string): void => {
    setEditingFieldId(null)
    if (onFieldUpdate) {
      const trimmedLabel = editingFieldLabel.trim()

      const formRow = group.fields.find(
        (f) => isFormRowField(f) && f.fields.some((ff) => ff.id === fieldId),
      ) as FormRowField | undefined

      if (formRow) {
        const updatedFormRow = {
          ...formRow,
          fields: formRow.fields.map((f) =>
            f.id === fieldId ? { ...f, label: trimmedLabel } : f,
          ),
        }
        const updatedGroup = {
          ...group,
          fields: group.fields.map((f) =>
            f.id === formRow.id ? updatedFormRow : f,
          ),
        }
        onUpdate(updatedGroup)
      } else {
        const field = group.fields.find((f) => f.id === fieldId)
        if (
          field &&
          'type' in field &&
          !('fields' in field) &&
          trimmedLabel === field.type
        ) {
          onFieldUpdate(group.id, fieldId, '')
        } else {
          onFieldUpdate(group.id, fieldId, trimmedLabel)
        }
      }
    }
  }

  const handleFieldLabelKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    fieldId: string,
  ): void => {
    if (e.key === 'Enter') {
      handleFieldLabelBlur(fieldId)
    } else if (e.key === 'Escape') {
      setEditingFieldId(null)
      setEditingFieldLabel('')
    }
  }

  const updateFieldLabelFromOption = (
    fieldId: string,
    optionLabel: string,
    autoFillSource?: PatientInfoField | null,
  ): void => {
    const trimmedLabel = optionLabel.trim()
    if (!trimmedLabel) return

    const loc = findFieldInGroup(group, fieldId)
    if (!loc) return

    if (loc.source === 'formRow') {
      const updatedFormRow: FormRowField = {
        ...loc.formRow,
        fields: loc.formRow.fields.map((f) =>
          f.id === fieldId
            ? {
                ...f,
                label: trimmedLabel,
                ...(autoFillSource !== undefined ? { autoFillSource } : {}),
              }
            : f,
        ),
      }
      const updatedGroup: GroupField = {
        ...group,
        fields: group.fields.map((f) =>
          f.id === loc.formRowId ? updatedFormRow : f,
        ),
      }
      onUpdate(updatedGroup)
      return
    }

    if (onFieldUpdate) {
      onFieldUpdate(group.id, fieldId, trimmedLabel)
      return
    }

    const updatedGroup: GroupField = {
      ...group,
      fields: group.fields.map((f) =>
        f.id === fieldId && !('fields' in f)
          ? { ...f, label: trimmedLabel }
          : f,
      ),
    }
    onUpdate(updatedGroup)
  }

  const handleFieldDragStart = (
    e: React.DragEvent<HTMLElement>,
    fieldId: string,
  ): void => {
    e.stopPropagation()
    setDraggedFieldId(fieldId)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', fieldId)
    e.dataTransfer.setData('drag-type', 'field')
    e.currentTarget
      .closest('.group-field-item, .form-row-field-item')
      ?.classList.add('dragging-field')
  }

  const handleFieldDragEnd = (e: React.DragEvent<HTMLElement>): void => {
    e.currentTarget
      .closest('.group-field-item, .form-row-field-item')
      ?.classList.remove('dragging-field')
    setDraggedFieldId(null)
    setDragOverFieldId(null)
  }

  const handleFormRowDragStart = (
    e: React.DragEvent<HTMLElement>,
    formRowId: string,
  ): void => {
    e.stopPropagation()
    setDraggedFormRowId(formRowId)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', formRowId)
    e.dataTransfer.setData('drag-type', 'form-row')
    e.currentTarget
      .closest('.form-row-container')
      ?.classList.add('dragging-field')
  }

  const handleFormRowDragEnd = (e: React.DragEvent<HTMLElement>): void => {
    e.currentTarget
      .closest('.form-row-container')
      ?.classList.remove('dragging-field')
    setDraggedFormRowId(null)
    setDragOverFieldId(null)
  }

  const handleFieldDragOver = (
    e: React.DragEvent<HTMLDivElement>,
    targetFieldId: string,
  ): void => {
    e.preventDefault()
    e.stopPropagation()

    if (draggedFieldId && draggedFieldId !== targetFieldId) {
      setDragOverFieldId(targetFieldId)
      return
    }

    if (draggedFormRowId && draggedFormRowId !== targetFieldId) {
      setDragOverFieldId(targetFieldId)
      return
    }

    const effectAllowed = e.dataTransfer.effectAllowed
    if (
      effectAllowed === 'copy' ||
      effectAllowed === 'all' ||
      !draggedFieldId
    ) {
      setDragOverFieldId(targetFieldId)
    }
  }

  const handleFieldDrop = (
    e: React.DragEvent<HTMLDivElement>,
    targetFieldId: string,
  ): void => {
    e.preventDefault()
    e.stopPropagation()

    let isNewComponent = false
    try {
      const jsonData = e.dataTransfer.getData('application/json')
      if (jsonData) {
        isNewComponent = true
      }
    } catch {
      // Not JSON data, continue to check for field reordering
    }

    if (isNewComponent) {
      try {
        const data = e.dataTransfer.getData('application/json')
        if (data) {
          const componentData = JSON.parse(data)
          const componentType = componentData.type as FormComponentType

          const targetIndex = group.fields.findIndex(
            (f) => f.id === targetFieldId,
          )

          if (targetIndex !== -1) {
            if (componentType === 'Group Fields') {
              setDragOverFieldId(null)
              return
            } else if (componentType === 'Form Row') {
              const newFormRow = {
                id: createFormBuilderId('form-row'),
                type: 'Form Row' as const,
                fields: [],
                columnCount: 2 as const,
              }
              const updatedFields = [...group.fields]
              updatedFields.splice(targetIndex, 0, newFormRow)
              onUpdate({ ...group, fields: updatedFields })
            } else {
              const newField = createFormField(
                componentType,
                createFormBuilderId('field'),
              )
              const updatedFields = [...group.fields]
              updatedFields.splice(targetIndex, 0, newField)
              onUpdate({ ...group, fields: updatedFields })
              onFieldSelect?.(newField)
            }
          }
        }
      } catch (error) {
        logger.error('Error handling component drop:', error)
      }
      setDragOverFieldId(null)
      return
    }

    if (draggedFieldId && draggedFieldId !== targetFieldId) {
      const sourceInfo = findFieldInGroup(group, draggedFieldId)
      const targetIndex = group.fields.findIndex((f) => f.id === targetFieldId)
      if (targetIndex !== -1 && sourceInfo) {
        if (sourceInfo.source === 'formRow') {
          const fieldToMove = sourceInfo.field
          const updatedFormRow = {
            ...sourceInfo.formRow,
            fields: sourceInfo.formRow.fields.filter(
              (f) => f.id !== draggedFieldId,
            ),
          }
          const updatedFields = group.fields.map((item) =>
            item.id === sourceInfo.formRowId ? updatedFormRow : item,
          )
          updatedFields.splice(targetIndex, 0, fieldToMove)
          onUpdate({ ...group, fields: updatedFields })
        } else if (sourceInfo.source === 'direct' && onFieldReorder) {
          onFieldReorder(group.id, draggedFieldId, targetIndex, parentGroupId)
        }
      }
    }

    if (draggedFormRowId && draggedFormRowId !== targetFieldId) {
      const currentIndex = group.fields.findIndex(
        (f) => f.id === draggedFormRowId,
      )
      const targetIndex = group.fields.findIndex((f) => f.id === targetFieldId)
      if (currentIndex !== -1 && targetIndex !== -1) {
        const reorderedFields = [...group.fields]
        const [movedFormRow] = reorderedFields.splice(currentIndex, 1)
        reorderedFields.splice(targetIndex, 0, movedFormRow)
        onUpdate({ ...group, fields: reorderedFields })
      }
      setDraggedFormRowId(null)
    }

    setDragOverFieldId(null)
  }

  const handleFieldDragLeave = (): void => {
    setDragOverFieldId(null)
  }

  const handleRichTextPlaceholderChange = (
    fieldId: string,
    nextPlaceholder: string,
  ): void => {
    if (isViewMode) return
    const updatedGroup = updateFieldPlaceholderInGroup(
      group,
      fieldId,
      nextPlaceholder,
    )
    onUpdate(updatedGroup)
  }

  const handleFormRowFieldDragOver = (
    e: React.DragEvent<HTMLDivElement>,
    formRow: FormRowField,
    targetFieldId: string,
  ): void => {
    e.preventDefault()
    e.stopPropagation()

    if (draggedFieldId && draggedFieldId !== targetFieldId) {
      const draggedFieldInFormRow = formRow.fields.some(
        (f) => f.id === draggedFieldId,
      )
      if (draggedFieldInFormRow) {
        setDragOverFieldId(targetFieldId)
        return
      }
    }

    const effectAllowed = e.dataTransfer.effectAllowed
    if (
      effectAllowed === 'copy' ||
      effectAllowed === 'all' ||
      !draggedFieldId
    ) {
      setDragOverFieldId(targetFieldId)
    }
  }

  const formRowColumnCount = (formRow: FormRowField): 1 | 2 =>
    formRow.columnCount === 1 ? 1 : 2

  const canPlaceInFormRow = (componentType: FormComponentType): boolean =>
    componentType !== 'Group Fields' &&
    componentType !== 'Form Row' &&
    componentType !== 'Rich Text'

  const placeFieldInFormRow = (
    formRow: FormRowField,
    field: FormField,
    insertIndex?: number,
  ): void => {
    if (formRow.fields.length >= formRowColumnCount(formRow)) return
    const nextFields = [...formRow.fields]
    const index =
      insertIndex == null
        ? nextFields.length
        : Math.min(Math.max(insertIndex, 0), nextFields.length)
    nextFields.splice(index, 0, field)
    onUpdate({
      ...group,
      fields: group.fields.map((item) =>
        item.id === formRow.id ? { ...formRow, fields: nextFields } : item,
      ),
    })
    onFieldSelect?.(field)
  }

  const handleFormRowComponentDrop = (
    e: React.DragEvent<HTMLDivElement>,
    formRow: FormRowField,
    insertIndex?: number,
  ): boolean => {
    const raw = e.dataTransfer.getData('application/json')
    if (!raw) return false
    const componentType = (JSON.parse(raw) as { type?: FormComponentType }).type
    if (componentType == null || !canPlaceInFormRow(componentType)) return true
    const newField = createFormField(
      componentType,
      createFormBuilderId('field'),
    )
    placeFieldInFormRow(formRow, newField, insertIndex)
    return true
  }

  const handleFormRowFieldDrop = (
    e: React.DragEvent<HTMLDivElement>,
    formRow: FormRowField,
    formRowId: string,
    targetFieldId: string,
  ): void => {
    e.preventDefault()
    e.stopPropagation()

    let isNewComponent = false
    try {
      const jsonData = e.dataTransfer.getData('application/json')
      if (jsonData) {
        isNewComponent = true
      }
    } catch {
      // Not JSON data, continue to check for field reordering
    }

    if (isNewComponent) {
      try {
        const data = e.dataTransfer.getData('application/json')
        if (data) {
          const componentData = JSON.parse(data)
          const componentType = componentData.type as FormComponentType

          if (componentType === 'Rich Text' && onDrop) {
            onDrop(e, group.id)
            setDragOverFieldId(null)
            return
          }

          if (canPlaceInFormRow(componentType)) {
            const targetIndex = formRow.fields.findIndex(
              (f) => f.id === targetFieldId,
            )
            if (targetIndex !== -1) {
              placeFieldInFormRow(
                formRow,
                createFormField(componentType, createFormBuilderId('field')),
                targetIndex,
              )
            }
          }
        }
      } catch (error) {
        logger.error('Error handling form row component drop:', error)
      }
      setDragOverFieldId(null)
      return
    }

    if (draggedFieldId && draggedFieldId !== targetFieldId) {
      const sourceInfo = findFieldInGroup(group, draggedFieldId)
      const targetIndex = formRow.fields.findIndex(
        (f) => f.id === targetFieldId,
      )
      if (targetIndex !== -1 && sourceInfo) {
        const draggedFieldInFormRow = formRow.fields.some(
          (f) => f.id === draggedFieldId,
        )
        if (draggedFieldInFormRow) {
          const currentIndex = formRow.fields.findIndex(
            (f) => f.id === draggedFieldId,
          )
          if (currentIndex !== -1) {
            const updatedFormRow = {
              ...formRow,
              fields: [...formRow.fields],
            }
            const [movedField] = updatedFormRow.fields.splice(currentIndex, 1)
            updatedFormRow.fields.splice(targetIndex, 0, movedField)
            const updatedGroup = {
              ...group,
              fields: group.fields.map((f) =>
                f.id === formRowId ? updatedFormRow : f,
              ),
            }
            onUpdate(updatedGroup)
          }
        } else {
          const fieldToMove = sourceInfo.field
          let withoutField: (FormField | GroupField | FormRowField)[]
          if (sourceInfo.source === 'direct') {
            withoutField = group.fields.filter((_, i) => i !== sourceInfo.index)
          } else {
            const updatedSourceFormRow = {
              ...sourceInfo.formRow,
              fields: sourceInfo.formRow.fields.filter(
                (f) => f.id !== draggedFieldId,
              ),
            }
            withoutField = group.fields.map((item) =>
              item.id === sourceInfo.formRowId ? updatedSourceFormRow : item,
            )
          }
          const updatedFormRow = {
            ...formRow,
            fields: [...formRow.fields],
          }
          updatedFormRow.fields.splice(targetIndex, 0, fieldToMove)
          const finalFields = withoutField.map((item) =>
            item.id === formRowId ? updatedFormRow : item,
          )
          onUpdate({ ...group, fields: finalFields })
        }
      }
    }

    setDragOverFieldId(null)
  }

  const handleFormRowFieldDragLeave = (): void => {
    setDragOverFieldId(null)
  }

  const getFieldIcon = (fieldType: FormComponentType): string => {
    return COMPONENT_ICONS[fieldType] || 'mgc_dots_fill'
  }

  const GroupDragHandleIcon = (): React.ReactElement => (
    <svg
      width="18"
      height="20"
      viewBox="0 0 18 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="group-drag-handle-icon"
    >
      <path
        d="M-2.18557e-07 5L5 -5.68248e-07L10 5L8.6 6.425L6 3.825L6 19L4 19L4 3.825L1.4 6.425L-2.18557e-07 5ZM8 15L9.4 13.575L12 16.175L12 1L14 1L14 16.175L16.6 13.575L18 15L13 20L8 15Z"
        fill="currentColor"
      />
    </svg>
  )

  return (
    <div
      className={`group-fields-container ${
        standalone ? 'standalone' : ''
      } ${isGroupDragging ? 'dragging-group' : ''} ${
        isGroupDragOver ? 'drag-over-group' : ''
      }`}
      onDragOver={
        level === 0 && onGroupDragOver
          ? (e) => {
              if (isGroupDragging !== undefined) {
                onGroupDragOver(e, group.id)
              }
            }
          : undefined
      }
      onDrop={
        level === 0 && (onGroupDrop || onDrop)
          ? (e) => {
              const dragType = e.dataTransfer.getData('drag-type')
              if (dragType === 'group' && onGroupDrop) {
                onGroupDrop(e, group.id)
              } else if (
                (e.dataTransfer.types.includes('application/json') ||
                  e.dataTransfer.types.includes('text/plain')) &&
                onDrop
              ) {
                onDrop(e, group.id)
              }
            }
          : undefined
      }
      data-group-id={group.id}
    >
      {!standalone && (
        <div className="group-fields-header">
          <div className="group-fields-header-left">
            {level === 0 && onGroupDragStart && !isViewMode && (
              <div
                className="group-fields-drag-handle"
                draggable
                onDragStart={(e) => {
                  onGroupDragStart(e, group.id)
                  e.stopPropagation()
                }}
                onDragEnd={onGroupDragEnd}
              >
                <GroupDragHandleIcon />
              </div>
            )}
            {isRichTextOnlyGroup(group) ? (
              <span className="group-fields-name group-fields-name-richtext-only">
                {group.name.trim().length > 0
                  ? `Single rich text: ${group.name}`
                  : 'Single rich text'}
              </span>
            ) : isEditing ? (
              <input
                type="text"
                value={groupName}
                onChange={handleNameChange}
                onBlur={handleNameBlur}
                onKeyDown={handleNameKeyDown}
                onFocus={handleGroupNameInputFocus}
                className="group-fields-name-input"
                autoFocus
              />
            ) : (
              <span
                className="group-fields-name"
                onClick={isViewMode ? undefined : () => setIsEditing(true)}
                onDoubleClick={
                  isViewMode ? undefined : () => setIsEditing(true)
                }
              >
                {group.name.trim().length > 0
                  ? `Group fields: ${group.name}`
                  : 'Group fields'}
              </span>
            )}
          </div>
          {!isViewMode && (
            <div className="group-fields-header-right">
              {!isRichTextOnlyGroup(group) && (
                <button
                  className="group-fields-icon-button"
                  onClick={() => onCopy(group)}
                  title="Copy"
                >
                  <i className="mgc mgc_copy_2_line"></i>
                </button>
              )}
              <button
                className="group-fields-icon-button"
                onClick={handleGroupDeleteClick}
                title="Delete"
              >
                <i className="mgc mgc_delete_2_fill"></i>
              </button>
            </div>
          )}
        </div>
      )}

      <div
        className={`group-fields-content ${isDraggingOver ? 'drag-over' : ''} ${
          level > 0 ? 'nested-group' : ''
        }`}
        onDrop={
          !isRichTextOnlyGroup(group) && onDrop
            ? (e) => {
                if (!hasGroupBeingDragged) {
                  try {
                    const data = JSON.parse(
                      e.dataTransfer.getData('application/json'),
                    )
                    if (data.type === 'Group Fields') {
                      return
                    }
                  } catch {
                    // Not a component drop, continue
                  }
                  onDrop(e, group.id)
                }
              }
            : undefined
        }
        onDragOver={
          !isRichTextOnlyGroup(group) && onDragOver
            ? (e) => {
                if (!hasGroupBeingDragged) {
                  onDragOver(e)
                }
              }
            : undefined
        }
        onDragLeave={!isRichTextOnlyGroup(group) ? onDragLeave : undefined}
        data-parent-group-id={parentGroupId}
      >
        {group.fields.length === 0 ? (
          <div className="group-fields-empty">
            <p className="mb-0">Drop components here</p>
          </div>
        ) : (
          <div className="group-fields-fields-list">
            {group.fields.map((item) => {
              if (isGroupField(item)) {
                return (
                  <GroupFields
                    key={item.id}
                    group={item}
                    onUpdate={(updatedGroup) => {
                      if (onNestedGroupUpdate && parentGroupId) {
                        onNestedGroupUpdate(
                          parentGroupId,
                          item.id,
                          updatedGroup,
                        )
                      } else {
                        onUpdate(updatedGroup)
                      }
                    }}
                    onDelete={(nestedGroupId) => {
                      if (onNestedGroupDelete && parentGroupId) {
                        onNestedGroupDelete(parentGroupId, nestedGroupId)
                      } else {
                        onDelete(nestedGroupId)
                      }
                    }}
                    onCopy={onCopy}
                    onFieldAdd={onFieldAdd}
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    isDraggingOver={false}
                    onFieldUpdate={onFieldUpdate}
                    onFieldReorder={(gId, fId, idx) =>
                      onFieldReorder?.(gId, fId, idx, group.id)
                    }
                    onFieldDelete={onFieldDelete}
                    onGroupAdd={onGroupAdd}
                    onNestedGroupUpdate={onNestedGroupUpdate}
                    onNestedGroupDelete={onNestedGroupDelete}
                    selectedFieldId={selectedFieldId}
                    onFieldSelect={onFieldSelect}
                    parentGroupId={group.id}
                    level={level + 1}
                    isViewMode={isViewMode}
                  />
                )
              }

              if (isFormRowField(item)) {
                return (
                  <div
                    key={item.id}
                    className={`form-row-container ${
                      dragOverFieldId === item.id ? 'drag-over-field' : ''
                    } ${selectedFieldId === item.id ? 'selected-field' : ''}`}
                    onClick={
                      isViewMode
                        ? undefined
                        : (event) => {
                            const target = event.target as HTMLElement
                            if (
                              target.closest(
                                'button, .form-row-drag-handle, .form-row-field-item',
                              )
                            ) {
                              return
                            }
                            onFieldSelect?.(item)
                          }
                    }
                    onDragOver={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                    }}
                    onDrop={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      const dragType = event.dataTransfer.getData('drag-type')
                      if (
                        dragType === 'form-row' ||
                        dragType === 'field' ||
                        dragType === 'group'
                      ) {
                        return
                      }
                      try {
                        handleFormRowComponentDrop(event, item)
                      } catch (error) {
                        console.error('Error parsing drag data:', error)
                      }
                    }}
                    onDragLeave={handleFieldDragLeave}
                  >
                    <div className="form-row-header">
                      {!isViewMode && (
                        <i
                          className="mgc mgc_dots_fill form-row-drag-handle"
                          draggable
                          onDragStart={(e) =>
                            handleFormRowDragStart(e, item.id)
                          }
                          onDragEnd={handleFormRowDragEnd}
                        ></i>
                      )}
                      <i className="mgc mgc_rows_2_fill form-row-icon"></i>
                      <span className="form-row-label">Form Row</span>
                      {!isViewMode && onFieldDelete && (
                        <button
                          className="form-row-delete-btn"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleFormRowDeleteClick(item)
                          }}
                          title="Delete form row"
                        >
                          <i className="mgc mgc_delete_2_fill"></i>
                        </button>
                      )}
                    </div>
                    <div
                      className="form-row-fields"
                      onDrop={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        if (
                          e.dataTransfer.getData('drag-type') === 'form-row'
                        ) {
                          return
                        }
                        if (e.dataTransfer.getData('drag-type') === 'field') {
                          const fieldId = e.dataTransfer.getData('text/plain')
                          const sourceInfo = findFieldInGroup(group, fieldId)
                          if (sourceInfo) {
                            const alreadyInRow =
                              sourceInfo.source === 'formRow' &&
                              sourceInfo.formRowId === item.id
                            if (
                              !alreadyInRow &&
                              item.fields.length >= formRowColumnCount(item)
                            ) {
                              return
                            }
                            const fieldToMove = sourceInfo.field
                            const targetIndex = item.fields.length
                            let withoutField: (
                              | FormField
                              | GroupField
                              | FormRowField
                            )[]
                            if (sourceInfo.source === 'direct') {
                              withoutField = group.fields.filter(
                                (_, i) => i !== sourceInfo.index,
                              )
                            } else {
                              const updatedSourceFormRow = {
                                ...sourceInfo.formRow,
                                fields: sourceInfo.formRow.fields.filter(
                                  (f) => f.id !== fieldId,
                                ),
                              }
                              withoutField = group.fields.map((it) =>
                                it.id === sourceInfo.formRowId
                                  ? updatedSourceFormRow
                                  : it,
                              )
                            }
                            const updatedFormRow = {
                              ...item,
                              fields: [...item.fields],
                            }
                            updatedFormRow.fields.splice(
                              targetIndex,
                              0,
                              fieldToMove,
                            )
                            const finalFields = withoutField.map((it) =>
                              it.id === item.id ? updatedFormRow : it,
                            )
                            onUpdate({ ...group, fields: finalFields })
                          }
                          return
                        }
                        try {
                          handleFormRowComponentDrop(e, item)
                        } catch (error) {
                          logger.error('Error parsing drag data:', error)
                        }
                      }}
                      onDragOver={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                    >
                      {item.fields.map((field) => (
                        <div
                          key={field.id}
                          className={`form-row-field-item ${
                            dragOverFieldId === field.id
                              ? 'drag-over-field'
                              : ''
                          } ${
                            selectedFieldId === field.id ? 'selected-field' : ''
                          }`}
                          onClick={
                            isViewMode
                              ? undefined
                              : () => onFieldSelect?.(field)
                          }
                          onDragOver={(e) =>
                            handleFormRowFieldDragOver(e, item, field.id)
                          }
                          onDrop={(e) =>
                            handleFormRowFieldDrop(e, item, item.id, field.id)
                          }
                          onDragLeave={handleFormRowFieldDragLeave}
                        >
                          {!isViewMode && (
                            <i
                              className="mgc mgc_dots_fill form-row-field-drag-handle"
                              draggable
                              onClick={(e) => e.stopPropagation()}
                              onDragStart={(e) =>
                                handleFieldDragStart(e, field.id)
                              }
                              onDragEnd={handleFieldDragEnd}
                            ></i>
                          )}
                          <i
                            className={`mgc ${getFieldIcon(field.type)} form-row-field-icon`}
                          ></i>
                          {editingFieldId === field.id ? (
                            <input
                              type="text"
                              value={editingFieldLabel}
                              onChange={handleFieldLabelChange}
                              onBlur={() => handleFieldLabelBlur(field.id)}
                              onKeyDown={(e) =>
                                handleFieldLabelKeyDown(e, field.id)
                              }
                              onFocus={handleFieldInputFocus}
                              className="form-row-field-item-input"
                              autoFocus
                              placeholder="Enter field name..."
                            />
                          ) : (
                            <span
                              className="form-row-field-item-name"
                              onDoubleClick={
                                isViewMode
                                  ? undefined
                                  : () => handleFieldLabelClick(field)
                              }
                            >
                              {field.label || field.type}
                            </span>
                          )}
                          {!isViewMode &&
                            FIELD_TYPES_WITH_CHEVRON.includes(field.type) && (
                              <i
                                className="mgc mgc_down_line group-field-chevron"
                                aria-hidden="true"
                              ></i>
                            )}
                          {!isViewMode && editingFieldId !== field.id && (
                            <div className="form-row-field-actions">
                              {onFieldOptionsUpdate &&
                                FIELD_TYPES_WITH_OPTIONS.includes(
                                  field.type,
                                ) && (
                                  <button
                                    type="button"
                                    className="form-row-field-options-btn"
                                    onClick={(e) =>
                                      openFieldOptionsPopover(e, field.id)
                                    }
                                    title="Field options"
                                  >
                                    <i className="mgc mgc_down_line"></i>
                                  </button>
                                )}
                              <button
                                className="form-row-field-delete-btn"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  const updatedFormRow = {
                                    ...item,
                                    fields: item.fields.filter(
                                      (f) => f.id !== field.id,
                                    ),
                                  }
                                  const updatedGroup = {
                                    ...group,
                                    fields: group.fields.map((f) =>
                                      f.id === item.id ? updatedFormRow : f,
                                    ),
                                  }
                                  onUpdate(updatedGroup)
                                  if (selectedFieldId === field.id) {
                                    onFieldSelect?.(null)
                                  }
                                }}
                                title="Delete field"
                              >
                                <i className="mgc mgc_delete_2_fill"></i>
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                      {!isViewMode &&
                        Array.from({
                          length: Math.max(
                            0,
                            formRowColumnCount(item) - item.fields.length,
                          ),
                        }).map((_, slotIndex) => (
                          <div
                            key={`${item.id}-slot-${slotIndex}`}
                            className="form-row-slot-empty"
                          >
                            Drop field
                          </div>
                        ))}
                    </div>
                  </div>
                )
              }

              const field = item as FormField

              if (field.type === 'Rich Text') {
                return (
                  <div
                    key={field.id}
                    className={`group-field-richtext-only ${
                      selectedFieldId === field.id ? 'selected-field' : ''
                    }`}
                    data-field-id={field.id}
                    onClick={
                      isViewMode ? undefined : () => onFieldSelect?.(field)
                    }
                  >
                    <div
                      className={`group-field-richtext-container ${
                        isViewMode
                          ? 'group-field-richtext-container--readonly'
                          : ''
                      }`}
                    >
                      <RichTextEditor
                        variant="email-fax"
                        value={field.placeholder ?? ''}
                        onChange={(next) =>
                          handleRichTextPlaceholderChange(field.id, next)
                        }
                        placeholder="Enter paragraph text to display in the form"
                        toolbarGroups={FORM_BUILDER_RICH_TEXT_TOOLBAR}
                        bodyActions={[]}
                        footerActions={[]}
                        disabled={isViewMode}
                      />
                    </div>
                  </div>
                )
              }

              return (
                <div key={field.id} className="group-field-row">
                  {!isViewMode && (
                    <i
                      className="mgc mgc_dots_fill group-field-row-handle"
                      draggable
                      onDragStart={(e) => handleFieldDragStart(e, field.id)}
                      onDragEnd={handleFieldDragEnd}
                    ></i>
                  )}
                  <div
                    className={`group-field-item ${
                      dragOverFieldId === field.id ? 'drag-over-field' : ''
                    } ${selectedFieldId === field.id ? 'selected-field' : ''}`}
                    onClick={
                      isViewMode ? undefined : () => onFieldSelect?.(field)
                    }
                    onDragOver={(e) => handleFieldDragOver(e, field.id)}
                    onDrop={(e) => handleFieldDrop(e, field.id)}
                    onDragLeave={handleFieldDragLeave}
                  >
                    {!isViewMode && (
                      <i
                        className="mgc mgc_dots_fill group-field-drag-handle"
                        draggable
                        onClick={(e) => e.stopPropagation()}
                        onDragStart={(e) => handleFieldDragStart(e, field.id)}
                        onDragEnd={handleFieldDragEnd}
                      ></i>
                    )}
                    <i
                      className={`mgc ${getFieldIcon(field.type)} group-field-icon`}
                    ></i>
                    {editingFieldId === field.id ? (
                      <input
                        type="text"
                        value={editingFieldLabel}
                        onChange={handleFieldLabelChange}
                        onBlur={() => handleFieldLabelBlur(field.id)}
                        onKeyDown={(e) => handleFieldLabelKeyDown(e, field.id)}
                        onFocus={handleFieldInputFocus}
                        className="group-field-item-input"
                        autoFocus
                        placeholder="Enter field name..."
                      />
                    ) : (
                      <span
                        className="group-field-item-name"
                        onDoubleClick={
                          isViewMode
                            ? undefined
                            : () => handleFieldLabelClick(field)
                        }
                      >
                        {field.label || field.type}
                      </span>
                    )}
                    {!isViewMode &&
                      FIELD_TYPES_WITH_CHEVRON.includes(field.type) && (
                        <i
                          className="mgc mgc_down_line group-field-chevron"
                          aria-hidden="true"
                        ></i>
                      )}
                    {!isViewMode && (
                      <div className="group-field-actions">
                        {onFieldOptionsUpdate &&
                          FIELD_TYPES_WITH_OPTIONS.includes(field.type) && (
                            <button
                              type="button"
                              className="group-field-options-btn"
                              onClick={(e) =>
                                openFieldOptionsPopover(e, field.id)
                              }
                              title="Field options"
                            >
                              <i className="mgc mgc_down_line"></i>
                            </button>
                          )}
                        {onFieldDelete && (
                          <button
                            className="group-field-delete-btn"
                            onClick={(e) => {
                              e.stopPropagation()
                              onFieldDelete(group.id, field.id, parentGroupId)
                              if (selectedFieldId === field.id) {
                                onFieldSelect?.(null)
                              }
                            }}
                            title="Delete field"
                          >
                            <i className="mgc mgc_delete_2_fill"></i>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {!standalone && !isRichTextOnlyGroup(group) && (
        <div className="group-fields-switches">
          <label className="group-fields-switch-item">
            <ToggleSwitch
              aria-label="Require this field"
              checked={group.required ?? false}
              disabled={isViewMode}
              onChange={(next) => onUpdate({ ...group, required: next })}
            />
            <span>Require this field</span>
          </label>
          <label className="group-fields-switch-item">
            <ToggleSwitch
              aria-label="Auto-fill details from records"
              checked={group.autoFill ?? false}
              disabled={isViewMode}
              onChange={(next) => onUpdate({ ...group, autoFill: next })}
            />
            <span>Auto-fill details from records</span>
          </label>
        </div>
      )}

      {onFieldOptionsUpdate &&
        fieldOptionsPopoverFieldId &&
        (() => {
          const loc = findFieldInGroup(group, fieldOptionsPopoverFieldId)
          const popoverField = loc?.field
          if (!popoverField) return null
          const options = getAutofillOptionsForFieldType(popoverField.type)
          const effectiveValue = options.some(
            (option) => option.value === popoverField.autoFillSource,
          )
            ? popoverField.autoFillSource
            : null
          return (
            <FieldOptionsPopover
              target={fieldOptionsPopoverTargetRef.current}
              onClose={closeFieldOptionsPopover}
            >
              <div className="group-field-popover-section">
                <Checkbox
                  id={`popover-required-${popoverField.id}`}
                  label="Required"
                  checked={popoverField.required ?? false}
                  onChange={(event) => {
                    onFieldOptionsUpdate(group.id, popoverField.id, {
                      required: event.target.checked,
                    })
                  }}
                />
              </div>
              <RadioGroup
                className="group-field-popover-section"
                name={`autofill-${popoverField.id}`}
                label="Auto-fill from patient"
                orientation="vertical"
                value={effectiveValue ?? ''}
                options={options.map((option) => ({
                  value: option.value ?? '',
                  label: option.label,
                }))}
                onChange={(nextValue) => {
                  const option = options.find(
                    (item) => (item.value ?? '') === nextValue,
                  )
                  if (!option) return
                  const fieldLocation = findFieldInGroup(group, popoverField.id)
                  if (
                    option.value !== null &&
                    fieldLocation?.source === 'formRow'
                  ) {
                    updateFieldLabelFromOption(
                      popoverField.id,
                      option.label,
                      option.value,
                    )
                    return
                  }

                  onFieldOptionsUpdate(group.id, popoverField.id, {
                    autoFillSource: option.value,
                  })
                  if (option.value !== null) {
                    updateFieldLabelFromOption(popoverField.id, option.label)
                  }
                }}
              />
            </FieldOptionsPopover>
          )
        })()}
      <Modal
        open={pendingDelete != null}
        onClose={() => setPendingDelete(null)}
        size="sm"
        variant="confirmation"
        iconTone="danger"
        title={
          pendingDelete?.kind === 'form-row'
            ? 'Delete form row'
            : 'Delete group'
        }
        description={
          pendingDelete?.kind === 'form-row'
            ? pendingDelete.fieldCount === 1
              ? 'This form row has 1 field. Deleting the row removes it too.'
              : `This form row has ${pendingDelete.fieldCount} fields. Deleting the row removes them too.`
            : countFormItems(group.fields) === 1
              ? 'This group has 1 form item. Deleting the group removes it too.'
              : `This group has ${countFormItems(group.fields)} form items. Deleting the group removes them too.`
        }
        secondaryLabel="Cancel"
        primaryLabel="Delete"
        primaryDestructive
        onSecondaryClick={() => setPendingDelete(null)}
        onPrimaryClick={confirmPendingDelete}
      />
    </div>
  )
}

export default GroupFields
